import { useRef, useMemo, type FC } from "react";
import { getVisibleItems } from "./adminSidebarConfig";
import { useAdminSidebarStyles } from "./adminSidebar.styles";
import { useAdminCounts } from "./hooks/useAdminCounts";
import { useAdminSidebarKeyboard } from "./hooks/useAdminSidebarKeyboard";
import { AdminSidebarGroup } from "./AdminSidebarGroup";
import { AdminSidebarItem } from "./AdminSidebarItem";
import type { AdminSidebarRole } from "./adminSidebar.types";

interface IAdminSidebarProps {
  selectedKey: string;
  onSelect: (key: string) => void;
  userRole: AdminSidebarRole;
}

export const AdminSidebar: FC<IAdminSidebarProps> = ({
  selectedKey,
  onSelect,
  userRole,
}) => {
  const styles = useAdminSidebarStyles();
  const visibleCategories = useMemo(
    () => getVisibleItems(userRole),
    [userRole],
  );
  const counts = useAdminCounts();
  const navRef = useRef<HTMLElement>(null);
  const handleKeyDown = useAdminSidebarKeyboard({ navRef, onSelect });

  const allVisibleKeys = useMemo(
    () => visibleCategories.flatMap((cat) => cat.items.map((item) => item.key)),
    [visibleCategories],
  );

  return (
    <nav
      ref={navRef}
      className={styles.root}
      aria-label="Admin navigation"
      onKeyDown={handleKeyDown}
    >
      <div className={styles.header} aria-hidden="true">
        <h2 className={styles.headerTitle}>Admin</h2>
        <p className={styles.headerSubtitle}>Manage your platform</p>
      </div>
      <div className={styles.scrollContainer}>
        {visibleCategories.map((category) => (
          <AdminSidebarGroup key={category.name} name={category.name}>
            {category.items.map((item) => {
              const isFirst =
                allVisibleKeys.indexOf(item.key) === 0 &&
                (!selectedKey || !allVisibleKeys.includes(selectedKey));
              const isSelected = item.key === selectedKey;

              return (
                <AdminSidebarItem
                  key={item.key}
                  item={item}
                  isSelected={isSelected}
                  onSelect={onSelect}
                  count={item.hasBadge ? counts[item.key] : undefined}
                  tabIndex={isSelected || isFirst ? 0 : -1}
                />
              );
            })}
          </AdminSidebarGroup>
        ))}
      </div>
    </nav>
  );
};
